import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { AppNewsDetailResponse, getCommonPageProps, getNews, getNewsDetail, getPostCategories } from '@/shared/api';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Constructor from '@/shared/components/constructor/Constructor';
import { AppConstructor } from '@/shared/components/constructor/Constructor/Constructor';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import SubscribePopup, { SUBSCRIBE_POPUP_NAME } from '@/shared/components/popups/SubscribePopup/SubscribePopup';
import ConstructorNewsSection from '@/shared/components/sections/ConstructorNewsSection';
import { NewsSectionProps } from '@/shared/components/sections/NewsSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import Button from '@/shared/components/shared/Button';
import Typografed from '@/shared/components/shared/Typografed';
import { DICT } from '@/shared/config/dict';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';

const WebShare = dynamic(() => import('@/shared/components/shared/WebShare'), { ssr: false });

const NewsDetailPage = ({
    breadcrumbs,
    content,
    title,
    description,
    сonstructorNewsSection,
    tag,
    date,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { openPopup } = useSetOpenedPopupsState();

    return (
        <DefaultLayout>
            <div className="news-detail-page-top js-header-theme-trigger" data-theme="dark">
                <div className="wrapper">
                    <Breadcrumbs className="news-page-breadcrumbs" breadcrumbs={breadcrumbs} />
                </div>

                <div className="wrapper news-detail-content-wrapper">
                    <div className="wrapper-inner news-detail-content">
                        <div className="news-detail-content__left">
                            <div className="news-detail-content__left-content">
                                <div className="news-detail-content__top text-xs">
                                    <div className="news-detail-content-top-text">{date}</div>
                                    <div className="news-detail-content-top-text">{tag}</div>
                                </div>
                                <div className="news-detail-content__bottom">
                                    <div className="news-detail-content__subscribe-block">
                                        <div className="news-page-description text-xs">
                                            <Typografed>
                                                {'Подпишитесь на рассылку, чтобы быть в курсе актуальных новостей'}
                                            </Typografed>
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            onClick={() => {
                                                openPopup(SUBSCRIBE_POPUP_NAME);
                                            }}
                                        >
                                            Подписаться
                                        </Button>
                                    </div>
                                    <WebShare label={'Поделиться:'} telegram vk className="news-detail-web-share" />
                                </div>
                            </div>
                        </div>
                        <div className="news-detail-content__right">
                            <h1 className="news-detail-content-title h2">
                                <Typografed>{title}</Typografed>
                            </h1>
                            <div className="news-detail-content-description news-detail-wrapper">
                                <Typografed>{description}</Typografed>
                            </div>
                            {content.length > 0 && <Constructor blocks={content} className="service-detail-section" />}
                        </div>
                    </div>
                </div>
                {сonstructorNewsSection.newsSlider.length > 0 && (
                    <ConstructorNewsSection newsSectionProps={сonstructorNewsSection} />
                )}
                <SubscribePopup />
            </div>
        </DefaultLayout>
    );
};

export default NewsDetailPage;

type NewsDetailPageProps = NonUndefined<
    CommonPageProps & {
        content: AppConstructor;
        сonstructorNewsSection: NewsSectionProps;
        title: string;
        description: string;
        tag: string;
        date: string;
    }
>;

export const getServerSideProps: GetServerSideProps<NewsDetailPageProps> = async ({ query }) => {
    const categorySlug = query.categorySlug as string;
    const slug = query.newsSlug as string;

    const [commonPageProps, postCategoriesResponse] = await Promise.all([getCommonPageProps(), getPostCategories()]);
    const categories = postCategoriesResponse.data || [];

    if (!categories.find((category) => category.slug === categorySlug)) {
        return { notFound: true };
    }

    const [newsDetailSettledResponse] = await Promise.allSettled([
        getNewsDetail({ params: { slug }, postCategories: categories }),
    ]);

    if (newsDetailSettledResponse.status === 'rejected') {
        const rejectedNewsDetailSettledResponse = newsDetailSettledResponse as PromiseRejectedResult;
        if (rejectedNewsDetailSettledResponse.reason?.response?.status === 404) {
            return { notFound: true };
        } else {
            if (rejectedNewsDetailSettledResponse.reason instanceof Error) {
                throw rejectedNewsDetailSettledResponse.reason;
            } else {
                throw new Error(DICT.INTERNAL_SERVER_ERROR['ru']);
            }
        }
    }

    const newsDetailResponse = (newsDetailSettledResponse as PromiseFulfilledResult<AppNewsDetailResponse>).value;

    const newsResponse = await getNews({
        params: { limit: 4, not_id: newsDetailResponse.data?.id || -1 },
        postCategories: postCategoriesResponse.data || [],
    });

    const title = newsDetailResponse.data?.title || '';

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'discounts-detail-page',
            meta: {
                ...commonPageProps.meta,
                title: newsDetailResponse.data?.meta.title || title,
                description: newsDetailResponse.data?.meta.description || '',
                keywords: newsDetailResponse.data?.meta.keywords || '',
                og_title: newsDetailResponse.data?.meta.og_title || title,
                og_description: newsDetailResponse.data?.meta.og_description || '',
                tw_title: newsDetailResponse.data?.meta.tw_title || title,
                tw_description: newsDetailResponse.data?.meta.tw_description || '',
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Новости', href: '/news/' },
                {
                    text: categories.find((category) => category.slug === categorySlug)?.name || '',
                    href: `/news/${categorySlug}/`,
                },
                { text: title, href: `/news/${categorySlug}/${slug}/` },
            ],
            сonstructorNewsSection: {
                title: 'Читайте также',
                btnSlug: '/news/',
                newsSlider: newsResponse.data?.items || [],
            },
            tag: newsDetailResponse.data?.categories.join(' / ') || '',
            date: newsDetailResponse.data?.date || '',
            content: newsDetailResponse.data?.content ?? [],
            title,
            description: newsDetailResponse.data?.description || '',
        } satisfies NewsDetailPageProps,
    };
};
